import React, { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import CurrencyFormat from "react-currency-format";
import DataTable from "@components/DataTable";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { AdminFormPage } from "@components/CatalogIndex/AdminFormPage";
import { ProductPricingRow } from "../types/ProductPricing";
import useStyles from "../styles/useStyles";

interface Props {
  products: ProductPricingRow[];
  currencyUnit: string;
  isLoading: boolean;
}

const Show: React.FC<Props> = ({ products, currencyUnit, isLoading }) => {
  const styles = useStyles();

  const displayRows: ProductPricingRow[] = useMemo(() => {
    if (products.length === 0) return products;

    const totalCost = products.reduce((sum, p) => sum + p.extCost, 0);
    const totalPrice = products.reduce((sum, p) => sum + p.extPrice, 0);
    const totalProfit = totalPrice - totalCost;
    const totalMarginVal =
      totalPrice > 0 ? (totalProfit / totalPrice) * 100 : 0;

    const emptyRow: ProductPricingRow = {
      id: "",
      name: "",
      quantity: 0,
      mfrPrice: "",
      discount: "",
      blendedCost: 0,
      markup: "",
      blendedPrice: 0,
      extCost: 0,
      extPrice: 0,
    };

    return [
      ...products,
      {
        ...emptyRow,
        id: "total",
        name: "TOTAL",
        extCost: totalCost,
        extPrice: totalPrice,
        rowType: "total",
      },
      {
        ...emptyRow,
        id: "profit",
        name: "PROFIT",
        extPrice: totalProfit,
        rowType: "profit",
      },
      {
        ...emptyRow,
        id: "margin",
        name: "MARGIN",
        extPrice: totalMarginVal,
        rowType: "margin",
      },
    ];
  }, [products]);

  const formatCurrency = (value: number) => (
    <CurrencyFormat
      displayType="text"
      prefix={currencyUnit}
      isNumericString={true}
      thousandSeparator={true}
      value={
        value && !isNaN(value) ? value.toFixed(2) : parseFloat("0").toFixed(2)
      }
    />
  );

  const isSummary = (row: ProductPricingRow) =>
    row.rowType === "total" ||
    row.rowType === "profit" ||
    row.rowType === "margin";

  const columns: ColumnDef<ProductPricingRow>[] = [
    {
      header: "Product",
      cell(props) {
        if (isSummary(props.row.original)) return null;
        return <span>{props.row.original.name}</span>;
      },
    },
    {
      header: "Quantity",
      cell(props) {
        if (isSummary(props.row.original)) return null;
        return <span>{props.row.original.quantity}</span>;
      },
    },
    {
      header: "MFR Price",
      cell(props) {
        if (isSummary(props.row.original)) return null;
        const mfrPrice = props.row.original.mfrPrice;
        return (
          <span>
            {mfrPrice === "0.0" ? (
              "—"
            ) : (
              <CurrencyFormat
                displayType="text"
                prefix={currencyUnit}
                isNumericString={true}
                thousandSeparator={true}
                value={mfrPrice}
              />
            )}
          </span>
        );
      },
    },
    {
      header: "Discount",
      cell(props) {
        if (isSummary(props.row.original)) return null;
        const mfrPrice = props.row.original.mfrPrice;
        return (
          <span>
            {mfrPrice === "0.0" ? "—" : `${props.row.original.discount}%`}
          </span>
        );
      },
    },
    {
      header: "Blended Cost",
      cell(props) {
        if (isSummary(props.row.original)) return null;
        return <span>{formatCurrency(props.row.original.blendedCost)}</span>;
      },
    },
    {
      header: "Markup",
      cell(props) {
        if (isSummary(props.row.original)) return null;
        const mfrPrice = props.row.original.mfrPrice;
        return (
          <span>
            {mfrPrice === "0.0" ? "—" : `${props.row.original.markup}%`}
          </span>
        );
      },
    },
    {
      header: "Blended Price",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "total")
          return <span style={{ fontWeight: 700 }}>{row.name}</span>;
        if (row.rowType === "profit" || row.rowType === "margin")
          return (
            <span style={{ fontWeight: 700, color: "#418172" }}>
              {row.name}
            </span>
          );
        return <span>{formatCurrency(row.blendedPrice)}</span>;
      },
    },
    {
      header: "EXT Cost",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "total")
          return (
            <span style={{ fontWeight: 700, color: "#b90200" }}>
              {formatCurrency(row.extCost)}
            </span>
          );
        if (row.rowType === "profit" || row.rowType === "margin") return null;
        return <span>{formatCurrency(row.extCost)}</span>;
      },
    },
    {
      header: "EXT Price",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "total")
          return (
            <span style={{ fontWeight: 700 }}>
              {formatCurrency(row.extPrice)}
            </span>
          );
        if (row.rowType === "profit")
          return (
            <span style={{ fontWeight: 700, color: "#418172" }}>
              {formatCurrency(row.extPrice)}
            </span>
          );
        if (row.rowType === "margin")
          return (
            <span style={{ fontWeight: 700, color: "#418172" }}>
              {row.extPrice.toFixed(2)}%
            </span>
          );
        return <span>{formatCurrency(row.extPrice)}</span>;
      },
    },
  ];

  return (
    <div className={`${styles.page} ${styles.rightAlignColumns}`}>
      <AdminFormPage title="Product Pricing">
        {isLoading ? (
          <ScopeStackSpinner />
        ) : (
          <DataTable
            data={displayRows}
            columns={columns}
            selectable={false}
            striped
            hover
          />
        )}
      </AdminFormPage>
    </div>
  );
};

export default Show;
